import { EditOutlined } from "@mui/icons-material";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";

import { formatLimit } from "@/pages/configuration/pages/chemistry-ranges/hooks/useTableData";
import { CellInput } from "@/pages/inspections/sheet/components/ReadingCells";
import { focusedCellSx } from "@/shared/components/editable-table/EditableCell";
import {
	ActionButton,
	RowActions,
} from "@/shared/components/editable-table/RowActions";
import { useEditableRow } from "@/shared/components/editable-table/useEditableRow";
import { backgroundColor } from "@/utils/theme";
import {
	ChemistryDraft,
	ElementEvaluation,
	ElementEvaluationCode,
} from "../models/ChemistryDraft";
import { EVALUATION_TONES } from "./EvaluationChips";

const evaluateText = (
	text: string,
	min: number | null,
	max: number | null,
): ElementEvaluationCode | null => {
	if (min === null && max === null) return null;

	const trimmed = text.trim();
	const isBelowDetection = trimmed.startsWith("<");
	const reading = Number(trimmed.replace("<", "").trim());

	if (trimmed === "" || trimmed === "<" || Number.isNaN(reading))
		return "MISSING";
	if (isBelowDetection) return min !== null && reading <= min ? "LOW" : "OK";
	if (min !== null && reading < min) return "LOW";
	if (max !== null && reading > max) return "HIGH";
	return "OK";
};

const formatElementValue = (
	value: number | null,
	decimals: number | null,
	isBelowDetection: boolean,
	rawValue: string | null,
) => {
	if (value === null || value === undefined) return rawValue ?? "";
	const formatted = decimals == null ? String(value) : value.toFixed(decimals);
	return isBelowDetection ? `< ${formatted}` : formatted;
};

const RESULT_COLORS: Record<ElementEvaluationCode, string | undefined> = {
	OK: EVALUATION_TONES.in.color,
	LOW: EVALUATION_TONES.out.color,
	HIGH: EVALUATION_TONES.out.color,
	MISSING: EVALUATION_TONES.missing.color,
};

const RESULT_BACKGROUNDS: Record<ElementEvaluationCode, string | undefined> = {
	OK: undefined,
	LOW: EVALUATION_TONES.out.bgcolor,
	HIGH: EVALUATION_TONES.out.bgcolor,
	MISSING: EVALUATION_TONES.missing.bgcolor,
};

const noRangeSx = {
	color: EVALUATION_TONES.noRange.color,
	bgcolor: EVALUATION_TONES.noRange.bgcolor,
};

const resultSx = (code: ElementEvaluationCode | null) => ({
	fontWeight: code && code !== "OK" ? 700 : 400,
	color: code ? RESULT_COLORS[code] : undefined,
	bgcolor: code ? RESULT_BACKGROUNDS[code] : undefined,
});

const cellSx = {
	fontSize: 11,
	textAlign: "right",
	py: 0.5,
	px: 1,
	whiteSpace: "nowrap",
	fontVariantNumeric: "tabular-nums",
	borderRight: "1px solid",
	borderColor: "divider",
} as const;

const rowLabelSx = {
	...cellSx,
	textAlign: "left",
	color: "text.secondary",
	fontWeight: 600,
	width: "1%",
} as const;

const actionsSx = {
	...cellSx,
	textAlign: "center",
	width: "1%",
	"& > *": { display: "inline-flex", verticalAlign: "middle" },
} as const;

type Props = {
	elements: ElementEvaluation[];
	hasRequirements: boolean;
	canEdit: boolean;
	draft: ChemistryDraft | null;
	isDirty: boolean;
	isSaving: boolean;
	onStartEdit: () => void;
	onDraftChange: (draft: ChemistryDraft) => void;
	onCancel: () => void;
	onSave: () => void;
};

export const ChemistryResultsTable = ({
	elements,
	hasRequirements,
	canEdit,
	draft,
	isDirty,
	isSaving,
	onStartEdit,
	onDraftChange,
	onCancel,
	onSave,
}: Props) => {
	const blockedReason = isSaving
		? "Still saving, try again in a moment"
		: !isDirty
			? "No changes to save"
			: null;

	const editable = useEditableRow<HTMLTableRowElement>({
		isEditing: draft !== null,
		canStartEdit: canEdit && !isSaving,
		blockedReason,
		onEdit: onStartEdit,
		onCancel,
		onSave,
	});

	const setValue = (elementId: number, text: string) =>
		draft &&
		onDraftChange({
			...draft,
			Values: { ...draft.Values, [elementId]: text },
		});

	const showActions = canEdit || draft !== null;

	return (
		<TableContainer sx={{ width: "100%", overflowX: "auto" }}>
			<Table size="small" sx={{ width: "100%" }}>
				<TableHead>
					<TableRow>
						<TableCell sx={rowLabelSx} />
						{elements.map((element) => (
							<TableCell
								key={element.ElementId}
								sx={{ ...cellSx, color: "text.secondary" }}
								title={element.Description ?? element.ElementName}
							>
								{element.ElementName}
							</TableCell>
						))}
						{showActions && <TableCell sx={actionsSx} />}
					</TableRow>
				</TableHead>
				<TableBody>
					{draft ? (
						<TableRow
							{...editable.rowProps}
							sx={(theme) => ({
								backgroundColor,
								...focusedCellSx(theme),
							})}
						>
							<TableCell sx={rowLabelSx}>Result</TableCell>
							{elements.map((element) => {
								const text = draft.Values[element.ElementId] ?? "";
								const code = evaluateText(
									text,
									element.MinValue,
									element.MaxValue,
								);

								return (
									<TableCell
										key={element.ElementId}
										sx={{ ...cellSx, ...resultSx(code), minWidth: 56 }}
									>
										<CellInput
											align="right"
											value={text}
											inputProps={{ inputMode: "decimal" }}
											sx={{
												fontSize: 11,
												color: "inherit",
												fontWeight: "inherit",
											}}
											onChange={(event) => {
												if (
													/^<?\s?\d{0,3}(\.\d{0,5})?$/.test(event.target.value)
												)
													setValue(element.ElementId, event.target.value);
											}}
										/>
									</TableCell>
								);
							})}
							<TableCell sx={{ ...actionsSx, backgroundColor }}>
								<RowActions
									isEditing
									editTitle="Edit chemistry"
									deleteTitle=""
									blockedReason={blockedReason}
									isReasonShown={editable.isReasonShown}
									onCancel={onCancel}
									onSave={editable.save}
								/>
							</TableCell>
						</TableRow>
					) : (
						<TableRow
							{...editable.rowProps}
							sx={
								editable.canStartEdit
									? { "&:hover": { backgroundColor } }
									: undefined
							}
						>
							<TableCell sx={rowLabelSx}>Result</TableCell>
							{elements.map((element) => (
								<TableCell
									key={element.ElementId}
									sx={{ ...cellSx, ...resultSx(element.EvaluationCode) }}
								>
									{formatElementValue(
										element.Value,
										element.Decimals,
										element.IsBelowDetection,
										element.RawValue,
									) || "-"}
								</TableCell>
							))}
							{showActions && (
								<TableCell sx={actionsSx}>
									<ActionButton
										title={editable.canStartEdit ? "Edit chemistry" : ""}
										disabled={!editable.canStartEdit}
										onClick={() => editable.startEdit()}
									>
										<EditOutlined fontSize="small" />
									</ActionButton>
								</TableCell>
							)}
						</TableRow>
					)}
					{hasRequirements && (
						<TableRow>
							<TableCell sx={rowLabelSx}>Range</TableCell>
							{elements.map((element) => (
								<TableCell
									key={element.ElementId}
									sx={{
										...cellSx,
										color: "text.secondary",
										...(element.MinValue == null &&
											element.MaxValue == null &&
											noRangeSx),
									}}
								>
									{formatLimit(element)}
								</TableCell>
							))}
							{showActions && <TableCell sx={actionsSx} />}
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};
